/* eslint-disable camelcase */
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import {
  batch,
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Conversation,
  Paginator,
  selectTwilioClient,
  selectTwilioToken,
  setAllConversationsLoaded,
  setConversationsClient,
  setJwtToken,
} from 'models/twilio';
import jwtDecode from 'jwt-decode';
import ConversationsClient from '@twilio/conversations';
import { getTwilioJwt } from 'network/twilioRequests';
import { fetchContactList } from 'models/contacts';
import ChatOverlay from './ChatOverlay';
import useCurrentAttendee from './hooks/useCurrentAttendee';
import useTwilioEventHandler from './hooks/useTwilioEventHandler';
import useChatVisibility from './hooks/useChatVisibility';
import { BEHAVIOR } from './constants';
import useMutableRef from './hooks/useMutableRef';
import ConverastionContext from './ConversationContext';
import useIdleAndActiveTabHook from './hooks/useIdleAndActiveTabHook';

type EventHandlers = keyof ReturnType<typeof useTwilioEventHandler>;
const eventNameToHandlerMap = {
  connectionStateChanged: 'connectionStateChangedHandler',
  conversationJoined: 'conversationJoinedHandler',
  userSubscribed: 'userSubscribedHandler',
  userUpdated: 'userUpdatedHandler',
  tokenAboutToExpire: 'updateTokenHandler',
  conversationUpdated: 'converastionUpdatedHandler',
  conversationRemoved: 'conversationRemovedHandler',
  connectionError: 'connectionErrorHandler',
} as Record<string, EventHandlers>;

const Twilio: FC = () => {
  const dispatch = useDispatch();
  const { executeAllTasks } = useContext(ConverastionContext);
  const attendeeRef = useMutableRef(useCurrentAttendee());
  const twilioClient = useSelector(selectTwilioClient, shallowEqual);
  const reduxJwtToken = useSelector(selectTwilioToken, shallowEqual);
  const displayChat = useChatVisibility();
  const eventHandlers = useTwilioEventHandler();
  useIdleAndActiveTabHook();

  const fetchAllConversations = useCallback(async (
    cumulativeArray: Conversation[],
    paginatorCall: () => Promise<Paginator<Conversation>>,
  ) => {
    const page = await paginatorCall();
    cumulativeArray.push(...page.items);
    // Only hasNextPage is required
    // Checking hasPrevPage will cause infinite recursion
    if (page.hasNextPage) await fetchAllConversations(cumulativeArray, page.nextPage);
  }, []);

  const loadAllConversations = useCallback(async (client: ConversationsClient) => {
    // Get all conversations
    const conversations: Conversation[] = [];
    try {
      await fetchAllConversations(conversations, () => client.getSubscribedConversations({ pageSize: 50 }));
      await Promise.all(conversations.map((conversation) => eventHandlers.conversationJoinedHandler(conversation)));
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(setAllConversationsLoaded(true));
      executeAllTasks();
    }
  }, [dispatch, executeAllTasks, fetchAllConversations, eventHandlers]);

  useEffect(() => {
    if (twilioClient) {
      Object.entries(eventNameToHandlerMap)
        .forEach(([eventName, handlerKey]) => twilioClient.on(eventName, eventHandlers[handlerKey]));

      return () => Object.entries(eventNameToHandlerMap)
        .forEach(([eventName, handlerKey]) => twilioClient.off(eventName, eventHandlers[handlerKey]));
    }
    return () => {};
  }, [twilioClient, eventHandlers]);

  // Run only once when twilioClient is null
  useEffect(() => {
    const requireNewToken = () => {
      if (!reduxJwtToken) return true;
      const decodedJwt = jwtDecode(reduxJwtToken) as Record<string, any>;
      if (!decodedJwt?.exp || decodedJwt?.grants?.identity !== attendeeRef.current.uuid) return true;
      return Math.floor(Date.now() / 1000 + BEHAVIOR.JWT_MARGIN) > decodedJwt.exp;
    };
    const init = async () => {
      if (!attendeeRef.current.uuid) return;
      const jwtToken = requireNewToken() ? await getTwilioJwt(attendeeRef.current.uuid) : reduxJwtToken;
      if (jwtToken) {
        const client = await ConversationsClient.create(jwtToken);
        batch(() => {
          dispatch(setJwtToken(jwtToken));
          dispatch(fetchContactList(attendeeRef.current.uuid));
          dispatch(setConversationsClient(client));
        });
        await loadAllConversations(client);
      }
    };
    if (!twilioClient && displayChat) init();
    return async () => {
      if (twilioClient) {
        twilioClient.shutdown();
        dispatch(setConversationsClient(null));
      }
    };
  }, [twilioClient, displayChat]);

  if (!displayChat) return null;
  return <ChatOverlay />;
};

export default Twilio;
